import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { useExitTransition } from "../hooks/useExitTransition";
import { useDebajoDeMd } from "../hooks/useEsMobile";
import { useAltaDeProducto } from "../hooks/useAltaDeProducto";
import { AgregarVariosModal, type AccionesDeVarios } from "./AgregarVariosModal";
import { BotonCancelarDeAlta, BotonDeAlta } from "./PieFijoDeAlta";
import { PreguntaDeSalidaDeCarga } from "./PreguntaDeSalidaDeCarga";
import { ModalDeConsejos } from "./ModalDeConsejos";
import { CuerpoDeUnProducto, ResumenDeConsejos } from "../pages/SubirProductos";
import { useCargaDesdePdf } from "./CargaDesdePdf";

export type ModoDeSubida = "uno" | "varios";

const TEXTOS: Record<ModoDeSubida, { titulo: string; bajada: string }> = {
  uno: {
    titulo: "Subir un producto",
    bajada: "Cargá las fotos de un producto para empezar a crear contenido con él.",
  },
  varios: {
    titulo: "Subir varios productos",
    bajada: "Cargá muchos productos juntos para empezar a crear contenido con ellos.",
  },
};

/**
 * Subir uno o varios productos SIN salir del modo — ADR-2026-10-14-subir-productos-en-un-pop-up-desde-modelo.
 *
 * Las dos tarjetas de subida de la grilla de productos del modo Modelo abren esto. Adentro
 * va lo MISMO que las pantallas de Mi marca (`CuerpoDeUnProducto` y `AgregarVariosModal`
 * con `enVentana`, más el resumen de consejos), en el envase del pop-up "Elegí qué
 * agregar" del Agente: ventana de 980 centrada en escritorio, hoja entera en el teléfono,
 * cabecera con título y X. Los botones van en un pie fijo de la ventana.
 *
 * "Ver todos los consejos" abre los consejos en otro pop-up encima (`ModalDeConsejos`).
 *
 * Cerrar con la X, Escape o tocando afuera NO tira lo cargado de un producto (queda el
 * borrador de `useAltaDeProducto`, el mismo de Mi marca); "Cancelar" sí.
 */
export const ModalDeSubirProductos: React.FC<{
  /** Qué se sube; `null` = cerrado. */
  modo: ModoDeSubida | null;
  onCerrar: () => void;
  companyId: string | null;
  brandId: string | null;
  userPlan?: string | null;
  /** Hay productos nuevos: el host refresca su grilla. */
  onProductosNuevos: () => void;
}> = ({ modo, onCerrar, companyId, brandId, userPlan, onProductosNuevos }) => {
  const { mounted, animated } = useExitTransition(modo !== null, 200);
  // Mientras sale, `modo` ya es null: se sigue dibujando el último.
  const ultimoModo = useRef<ModoDeSubida>("uno");
  if (modo) ultimoModo.current = modo;
  const actual = modo ?? ultimoModo.current;

  const alta = useAltaDeProducto({ companyId, brandId });
  const [variosOcupado, setVariosOcupado] = useState(false);
  const [accionesVarios, setAccionesVarios] = useState<AccionesDeVarios | null>(null);
  // El PDF del proveedor, soltado en la zona de fotos (ADR-2026-10-18-el-pdf-entra-por-la-subida-de-siempre).
  const pdf = useCargaDesdePdf(alta);
  const ocupado = alta.guardando || variosOcupado || pdf.fase === "leyendo";
  const esEscritorio = !useDebajoDeMd();

  // Varios no tiene borrador: cerrar con productos cargados pregunta antes, como la
  // pantalla de Mi marca (ADR-2026-10-14-varios-pregunta-antes-de-salir).
  const cargadoEnVarios = actual === "varios" && !!accionesVarios?.hayCargado;
  const [preguntando, setPreguntando] = useState(false);
  // "Ver todos los consejos" abre los consejos en otro pop-up encima de éste: sin salir del
  // modo ni perder la carga (ADR-2026-10-14-consejos-en-un-pop-up-desde-varios).
  const [consejosAbiertos, setConsejosAbiertos] = useState(false);
  // Si crear desde la pregunta falla, el alta no termina: se cierra la pregunta para que
  // se vea el error.
  const creandoAntes = useRef(false);
  useEffect(() => {
    const creando = !!accionesVarios?.creando;
    if (creandoAntes.current && !creando) setPreguntando(false);
    creandoAntes.current = creando;
  }, [accionesVarios?.creando]);
  useEffect(() => {
    if (!modo) setPreguntando(false);
  }, [modo]);

  const cerrar = () => {
    if (ocupado) return;
    if (cargadoEnVarios) return setPreguntando(true);
    onCerrar();
  };
  const cancelar = () => {
    if (ocupado) return;
    if (cargadoEnVarios) return setPreguntando(true);
    if (actual === "uno") {
      alta.descartar();
      pdf.descartar();
    }
    onCerrar();
  };
  const guardarUno = async () => {
    if (await pdf.guardar()) {
      onProductosNuevos();
      onCerrar();
    }
  };

  // Escape cierra (como el pop-up del Agente), salvo con un guardado en vuelo.
  const cerrarRef = useRef(cerrar);
  cerrarRef.current = cerrar;
  const preguntandoRef = useRef(preguntando);
  preguntandoRef.current = preguntando;
  useEffect(() => {
    if (!modo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !preguntandoRef.current) cerrarRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modo]);

  if (!mounted) return null;
  const textos = TEXTOS[actual];

  return createPortal(
    <div
      // z-[210]: por encima del sheet del modo y de sus pantallas de mobile, como el drawer
      // de alta que reemplaza.
      className={`fixed inset-0 z-[210] flex items-end justify-center bg-black/40 p-0 backdrop-blur-[2px] transition-opacity duration-200 ease-out motion-reduce:transition-none md:items-center md:p-4 ${
        animated ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
      onClick={cerrar}
      role="dialog"
      aria-modal="true"
      aria-label={textos.titulo}
    >
      <div
        className={`flex w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none md:h-[min(88vh,760px)] md:max-w-[980px] md:rounded-[14px] md:border md:border-neutral-200 ${
          animated
            ? "translate-y-0 md:scale-100 md:opacity-100"
            : "translate-y-full md:translate-y-0 md:scale-95 md:opacity-0"
        }`}
        // En el teléfono, hasta arriba (lo mismo que "Elegí qué agregar" con
        // `altoEnElTelefono="completa"`). En `md` manda la clase.
        style={
          esEscritorio
            ? undefined
            : { height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))" }
        }
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera: qué se sube y la X. */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-black/[0.06] px-4 pb-3.5 pt-4 md:px-6">
          <div className="min-w-0">
            <h3 className="text-[17px] font-semibold leading-tight text-[#16181D]">
              {textos.titulo}
            </h3>
            <p className="mt-1 text-[13px] leading-snug text-black/50">{textos.bajada}</p>
          </div>
          <button
            type="button"
            onClick={cerrar}
            disabled={ocupado}
            aria-label="Cerrar"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-black/40 transition-colors hover:bg-black/[0.06] hover:text-black/70 disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Cuerpo: el fondo gris de la pantalla de Mi marca, para que las tarjetas blancas
            se lean igual. */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-[#F9F9F9] px-4 py-4 md:px-6 md:py-5">
          {actual === "uno" ? (
            <>
              <CuerpoDeUnProducto alta={alta} rubro={alta.rubro} enVentana pdf={pdf} />
              <ResumenDeConsejos onVerTodos={() => setConsejosAbiertos(true)} />
            </>
          ) : (
            <AgregarVariosModal
              open
              enVentana
              companyId={companyId}
              brandId={brandId}
              userPlan={userPlan ?? alta.plan}
              onClose={cancelar}
              onSaved={onProductosNuevos}
              onTerminado={onCerrar}
              onOcupado={setVariosOcupado}
              onAcciones={setAccionesVarios}
              // El aviso de Tiendanube abre su propia ventana: por encima de ésta.
              zIndex={220}
              alFinal={<ResumenDeConsejos onVerTodos={() => setConsejosAbiertos(true)} />}
            />
          )}
        </div>

        {/* Pie: Cancelar y la acción, siempre a la vista. */}
        <div
          className="shrink-0 border-t border-black/[0.06] bg-white px-4 pt-3 md:px-6"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
        >
          {actual === "uno" && alta.guardando && (
            <p className="pb-2 text-[13px] text-black/55">
              Guardando el producto. Puede tardar unos segundos.
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <BotonCancelarDeAlta onClick={cancelar} disabled={ocupado} />
            {actual === "uno" ? (
              <BotonDeAlta
                onClick={guardarUno}
                disabled={alta.producto.images.length === 0 || alta.guardando || !companyId || pdf.fase === "leyendo"}
              >
                {alta.guardando && <Loader2 className="h-4 w-4 animate-spin" />}
                {alta.guardando ? "Guardando…" : "Guardar producto"}
              </BotonDeAlta>
            ) : (
              <BotonDeAlta
                onClick={() => accionesVarios?.crear()}
                disabled={!accionesVarios?.puedeCrear}
              >
                {accionesVarios?.creando && <Loader2 className="h-4 w-4 animate-spin" />}
                {accionesVarios?.etiqueta ?? "Crear productos"}
              </BotonDeAlta>
            )}
          </div>
        </div>
      </div>
      <ModalDeConsejos abierto={consejosAbiertos} onCerrar={() => setConsejosAbiertos(false)} />
      {preguntando && (
        <PreguntaDeSalidaDeCarga
          etiquetaDeCrear={accionesVarios?.etiqueta ?? "Crear productos"}
          puedeCrear={!!accionesVarios?.puedeCrear}
          creando={!!accionesVarios?.creando}
          // Al terminar, el alta llama a `onTerminado` (= `onCerrar`).
          onCrear={() => accionesVarios?.crear()}
          onDescartar={() => {
            setPreguntando(false);
            onCerrar();
          }}
          onQuedarse={() => setPreguntando(false)}
        />
      )}
    </div>,
    document.body,
  );
};
